/**
 * Version checking service to detect when new code is deployed
 * Prevents stale cached versions from running
 */

interface VersionInfo {
  version: string;
  buildTime: string;
}

let currentVersion: VersionInfo | null = null;

/**
 * Initialize version check - call this on app startup
 */
export const initializeVersion = async (): Promise<VersionInfo> => {
  try {
    const response = await fetch('/version.json?t=' + Date.now(), {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    const data = await response.json();
    currentVersion = data;
    console.log('App version initialized:', currentVersion);
    return data;
  } catch (error) {
    console.error('Failed to initialize version:', error);
    // Return a default version if fetch fails
    currentVersion = { version: '1.0.0', buildTime: new Date().toISOString() };
    return currentVersion;
  }
};

/**
 * Check if a new version is available
 * Returns true if server version is different from current
 */
export const checkForNewVersion = async (): Promise<boolean> => {
  if (!currentVersion) {
    await initializeVersion();
    return false;
  }

  console.log('Checking for new version...', { currentVersion });
  try {
    const response = await fetch('/version.json?t=' + Date.now(), {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    const serverVersion: VersionInfo = await response.json();
    
    // Compare version string or buildTime to detect changes
    const hasNewVersion = serverVersion.version !== currentVersion.version ||
      serverVersion.buildTime !== currentVersion.buildTime;
    
    if (hasNewVersion) {
      console.log('New version detected!', {
        current: currentVersion,
        server: serverVersion
      });
    }
    
    return hasNewVersion;
  } catch (error) {
    console.error('Failed to check version:', error);
    return false;
  }
};

/**
 * Force reload the application with cache clear
 */
export const reloadApp = () => {
  // Clear cache and reload
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }
  
  // Force hard reload
  window.location.reload();
};

/**
 * Get current version info
 */
export const getCurrentVersion = (): VersionInfo | null => {
  return currentVersion;
};
